import React, { useEffect } from "react";
import { useFormUtils } from "@/hooks/useFormUtils";
import { toCammelCase } from "@/utils";
import classNames from "classnames";
import { FormField } from "./FormField";
import { FormSubGroup } from "./FormSubGroup";
import { FormCollapsibleGroup } from "./FormCollapsibleGroup";
import { FieldType, FormStepGroupType } from "@/data/forms/types";
import { FormMethodsTypes } from "../../Fields/types";

export const FormGroup = ({
  group,
  formValues,
  formSteps,
  t,
  formMethods,
  nextStepTriggerCount = 0,
}: {
  group: FormStepGroupType;
  formValues: any;
  formSteps: any;
  t: Record<string, string>;
  formMethods: FormMethodsTypes;
  nextStepTriggerCount?: number;
}) => {
  const { isFieldVisible, isSubGroupVisible, setRequiredFieldValidation } = useFormUtils();
  const {
    watch,
    register,
    unregister,
    trigger,
    setValue,
    errors,
    setError,
    clearErrors,
    touchedFields,
    isSubmitted,
    values,
  } = formMethods;

  const groupClassnames = classNames("u-grid Step__group", {
    [`group--${toCammelCase(group.name)}`]: group.name,
    [group.class as string]: group.class,
  });

  const isEmergencyContactStep = group?.name === "emergency-contact";
  const groupEmergencyContactFieldNames = group.fields.map((item) => item.name) || [];
  const valuesOfEmergencyContactGroups = watch(groupEmergencyContactFieldNames);
  const hasNonEmptyValueEmergencyContactGroup = valuesOfEmergencyContactGroups.some(
    (value) => value !== undefined && value !== null && value !== ""
  );

  const getEmergencyContactValidation = (field: FieldType) => {
    if (!hasNonEmptyValueEmergencyContactGroup) {
      return { ...field.validation, required: false };
    }

    const requiredValidation = setRequiredFieldValidation(field);
    return requiredValidation ?? field.validation;
  };

  useEffect(() => {
    if (!isEmergencyContactStep || nextStepTriggerCount === 0) return;

    void trigger(groupEmergencyContactFieldNames);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextStepTriggerCount]);

  useEffect(() => {
    if (!isEmergencyContactStep || hasNonEmptyValueEmergencyContactGroup) return;

    clearErrors(groupEmergencyContactFieldNames);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNonEmptyValueEmergencyContactGroup]);

  const groupBody = (
    <div className={groupClassnames}>
      {!group.collapsible && group.title && (
        <p className="Step__group--title big">{t[group.title]}</p>
      )}
      {!group.collapsible && group.subTitle && (
        <p className="u-grid-col-12 Step__group--subtitle medium">{t[group.subTitle]}</p>
      )}

      {group.fields.map((field: any) => {
        return isFieldVisible({ fieldName: field.name, formValues, formSteps }) ? (
          <FormField
            key={field.name}
            field={field}
            t={t}
            register={register}
            unregister={unregister}
            touchedFields={touchedFields}
            isSubmitted={isSubmitted}
            watch={watch}
            values={values}
            trigger={trigger}
            setValue={setValue}
            errors={errors}
            setError={setError}
            clearErrors={clearErrors}
            dynamicValidation={
              isEmergencyContactStep ? getEmergencyContactValidation(field) : undefined
            }
            nextStepTriggerCount={isEmergencyContactStep ? nextStepTriggerCount : undefined}
          />
        ) : null;
      })}

      {group.subGroups?.map((subGroup: any) =>
        isSubGroupVisible(subGroup, formValues) ? (
          <FormSubGroup
            key={subGroup.name}
            subGroup={subGroup}
            t={t}
            formMethods={formMethods}
          />
        ) : null
      )}
    </div>
  );

  return (
    <React.Fragment>
      {group.hasSeparationLine && <div className="separation-line"></div>}
      {group.collapsible ? (
        <FormCollapsibleGroup
          group={group}
          t={t}
          formValues={formValues}
          errors={errors}
          nextStepTriggerCount={nextStepTriggerCount}>
          {groupBody}
        </FormCollapsibleGroup>
      ) : (
        groupBody
      )}
    </React.Fragment>
  );
};
